import { useEffect, useRef } from "react";
import "./DragonClawAnimation.css";

export default function DragonClawAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (960px total ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Initial attacker animation (slight scale before attack)
    attackerEl.style.transition = 'transform 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const dragonClaw = document.createElement("div");
    dragonClaw.className = "dragon-claw-anim";
    dragonClaw.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    dragonClaw.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(dragonClaw);
    animRef.current = dragonClaw;
    
    // Create dragon effects at impact moment (frame 3)
    const dragonEffectTimer = setTimeout(() => {
      // Dragon flash effect
      const flash = document.createElement('div');
      flash.className = 'dragon-claw-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make defender react
      defenderEl.classList.add('dragon-claw-hit');
      
      // Remove flash and reset defender
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('dragon-claw-hit');
      }, 300);
      
      // Create dragon energy particles
      for (let i = 0; i < 12; i++) {
        const particle = document.createElement('div');
        particle.className = 'dragon-claw-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 30;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation and scale
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--scale', `${0.7 + Math.random() * 0.8}`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 600 + Math.random() * 400);
      }
      
      // Create dragon claw marks
      const clawMark = document.createElement('div');
      clawMark.className = 'dragon-claw-mark';
      clawMark.style.left = `${centerX}px`;
      clawMark.style.top = `${centerY}px`;
      container.appendChild(clawMark);
      
      // Auto-remove claw mark
      setTimeout(() => clawMark.remove(), 900);
      
    }, 300); // Sync with frame 3 impact

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.2s ease-out';
      attackerEl.style.transform = '';
    }, 500);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (dragonClaw.parentNode) {
        dragonClaw.remove();
      }
      onComplete();
    }, 900);

    // Cleanup function
    return () => {
      clearTimeout(dragonEffectTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('dragon-claw-hit');
      }
      
      if (dragonClaw.parentNode) {
        dragonClaw.remove();
      }
      
      // Remove any flash, particles or marks
      const elements = container.querySelectorAll('.dragon-claw-flash, .dragon-claw-particle, .dragon-claw-mark');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
DragonClawAnimation.moveType = 'dragon';
